/**
 * useZonePolygonDrawing
 *
 * JointJS-driven hook that binds paper pointer events to the Zone drawing
 * store. Renders a live preview path while the user clicks points and a
 * ghost line to the cursor. `completeDrawing()` finalizes the polygon,
 * persists it via `zoneApi.saveZoneMapping`, and resets the store.
 *
 * Integration notes:
 * - Caller (EditorPage) must pass `paper` and `graph`. Hook no-ops if either
 *   is null.
 * - Caller's other drawing hook (`useObjectCreation`) should early-return
 *   when `useZoneDrawingStore.getState().mode !== 'idle'` to avoid double
 *   handling of `blank:pointerdown` / `blank:pointerdblclick` events.
 *
 * Preview cells are tagged with `data._isZonePreview = true` and removed on
 * mode exit / drawing completion.
 */

import { useCallback, useEffect, useRef } from 'react';
import { dia, shapes } from '@joint/core';

import { computeLabelFontSize } from '../lib/parkingScaleMetrics';
import { linkZoneToContainedCctvs } from '../../../map-editor-features/editor/lib/zoneCctvRelations';

import { useZoneDrawingStore } from '../../../map-editor-shared/store/zoneDrawingStore';
import { getFlag } from '../../../config/featureFlags';
import {
  zoneApi,
  ZONE_DEFAULT_COLOR,
  ZONE_LAYER_NAME,
} from '../../../map-editor-shared/api/zoneApi';
import type { Point } from '../../../map-editor-shared/api/types/layerMapping';
import { LABEL_TEXT_COLOR } from '../lib/labelTextMetrics';
import { nextFloorZoneName, zoneNumberFromName } from '../../../map-editor-shared/lib/zoneName';

export const ZONE_PREVIEW_DATA_KEY = '_isZonePreview';
export const ZONE_FILL_OPACITY = 0.25;

/** Pending zone — points + auto-suggested name, passed to modal for user confirmation. */
export interface PendingZoneInput {
  points: Point[];
  suggestedName: string;
  existingZoneNames: string[];
}

/** User-confirmed values from the ZoneCreateModal. */
export interface ZoneFinalizeInput {
  name: string;
  /** Layer/group key (default: ZONE_LAYER_NAME). Reserved for future grouping. */
  group?: string;
  /** Stroke color (#hex). 미지정 시 createZoneCell 가 ZONE_DEFAULT_COLOR fallback. */
  color?: string;
}

interface UseZonePolygonDrawingArgs {
  paper: dia.Paper | null;
  graph: dia.Graph | null;
  onError?: (error: Error) => void;
  onSuccess?: (cellId: string) => void;
  /**
   * 완료 버튼 누르면 호출됨 — 사용자에게 모달을 띄워 group/name 을 받는 책임은 호출자.
   * 호출자가 입력을 받아 finalizeDrawing(input) 을 부르거나 cancelDrawing() 으로 취소.
   */
  onRequestFinalize?: (pending: PendingZoneInput) => void;
  /** 지금 층 이름(B1) — 새 구역 이름 앞에 붙는다. */
  floorName?: string;
}

interface UseZonePolygonDrawingResult {
  /** 완료 버튼 핸들러. 모달을 띄우거나(onRequestFinalize 있으면) 즉시 finalize. */
  completeDrawing: () => Promise<void>;
  /** 모달 confirm 후 호출 — 실제 polygon 생성 + API 저장 */
  finalizeDrawing: (input: ZoneFinalizeInput) => Promise<void>;
  cancelDrawing: () => void;
}

/** Build an SVG path `d` attribute for the preview line. */
export function buildPreviewPath(points: Point[], hover: Point | null): string {
  if (points.length === 0) return '';
  const segments: string[] = [];
  points.forEach((p, i) => {
    segments.push(i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`);
  });
  if (hover) segments.push(`L ${hover.x} ${hover.y}`);
  return segments.join(' ');
}

/** JointJS `refPoints` format: `"x1,y1 x2,y2 ..."`. */
export function buildRefPoints(points: Point[]): string {
  return points.map((p) => `${p.x},${p.y}`).join(' ');
}

/** bbox of point list. Returns null for empty input. */
export function pointsBBox(
  points: Point[]
): { minX: number; minY: number; width: number; height: number } | null {
  if (points.length === 0) return null;
  let minX = points[0].x;
  let minY = points[0].y;
  let maxX = points[0].x;
  let maxY = points[0].y;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  // Guard 0 width/height (collinear points) to keep JointJS happy.
  const width = Math.max(maxX - minX, 0.001);
  const height = Math.max(maxY - minY, 0.001);
  return { minX, minY, width, height };
}

/**
 * Zone polygon cell 을 생성하는 공통 helper. drawing 완료 / restore 양쪽에서 사용.
 *
 * 정책 (사용자 요청):
 * - position + size 명시 (JointJS 가 정상 렌더하도록)
 * - body.refPoints 는 bbox 기준 상대 좌표
 * - **fill 없음 (transparent)** — 외곽선만 그림
 * - stroke 만 ZONE_DEFAULT_COLOR 로 표시
 */
export function createZoneCell(args: {
  points: Point[];
  name: string;
  objectTypeId: number;
  entityHandle: string;
  layerName?: string;
  memo?: string;
  /** ObjectType.color (#hex). 미지정 시 ZONE_DEFAULT_COLOR. */
  strokeColor?: string;
  /**
   * 구역 이름 폰트 크기. 맵 좌표계는 수천~수만 단위라 고정 12px 는 화면에서
   * 사실상 보이지 않는다 — 호출자가 computeLabelFontSize(graph) 를 넘겨
   * 다른 라벨과 같은 배율로 맞춘다. 미지정 시 기존 고정값.
   */
  fontSize?: number;
}): dia.Element | null {
  const bbox = pointsBBox(args.points);
  if (!bbox) return null;
  const relativePoints = args.points.map((p) => ({
    x: p.x - bbox.minX,
    y: p.y - bbox.minY,
  }));
  const layer = args.layerName ?? ZONE_LAYER_NAME;
  const stroke = (args.strokeColor && args.strokeColor.trim()) || ZONE_DEFAULT_COLOR;

  const polygon = new shapes.standard.Polygon({
    position: { x: bbox.minX, y: bbox.minY },
    size: { width: bbox.width, height: bbox.height },
  });
  polygon.attr({
    body: {
      refPoints: buildRefPoints(relativePoints),
      // 사용자 요청: 안에 색 없이 선만
      fill: 'none',
      stroke,
      strokeWidth: 2,
    },
    label: {
      // 빨강 고정 — Light/Dark 배경 양쪽에서 보이게(LABEL_TEXT_COLOR).
      text: args.name,
      fill: LABEL_TEXT_COLOR,
      fontSize: args.fontSize && args.fontSize > 0 ? args.fontSize : 12,
      refY: '50%',
      refX: '50%',
      textAnchor: 'middle',
      textVerticalAnchor: 'middle',
    },
  });

  polygon.set('data', {
    typeId: args.objectTypeId,
    type: 'polygon',
    layer,
    properties: {
      kind: 'zone',
      closed: true,
      name: args.name,
      memo: args.memo,
      color: stroke,
    },
    points: args.points,
    // elementConverter.convertElementToMapObject 가 polygon geometry 를 만들
    // 때 data.vertices ([x, y] 튜플 배열) 를 읽는다. points (= {x,y}) 만 있으면
    // SVG body/d fallback 도 실패하고 point geometry 로 떨어져 transformToZone
    // 의 catch 블록이 (0,0)~(100,100) 고정 사각형을 export 한다. 사용자가 찍은
    // 모든 vertex 가 export 에 그대로 들어가도록 vertices 를 같이 저장한다.
    vertices: args.points.map((p) => [p.x, p.y] as [number, number]),
    // 사용자가 zone 을 드래그하면 element.position() 만 바뀌고 vertices 는
    // 고정이라 export 시 delta 보정이 필요. bbox 좌상단 = element.position()
    // 초기값을 originalPosition 으로 기록.
    originalPosition: { x: bbox.minX, y: bbox.minY },
    entity_handle: args.entityHandle,
  });
  polygon.prop('objectTypeId', args.objectTypeId);
  return polygon;
}

/**
 * 이미 그래프에 있는 zone cell 의 **폴리곤만** 새 점들로 교체한다.
 *
 * cell 을 지우고 새로 만들지 않는 이유: cell id 가 바뀌면 다른 객체가 이
 * 존을 참조하던 관계(zoneId/cctvIds)와 선택 상태가 끊긴다. 자동 생성이
 * 정해 준 소속(memberIds)을 유지한 채 모양만 바꾸는 게 이 기능의 전부라,
 * 기하 관련 필드만 제자리에서 갱신한다.
 *
 * 갱신 대상은 createZoneCell 이 기하로부터 만들어 내는 것 전부와 일치해야
 * 한다 — position/size/refPoints 는 렌더용, points/vertices/originalPosition
 * 은 export 용이라 하나라도 빠지면 화면과 저장본이 어긋난다.
 */
export function applyZonePoints(cell: dia.Element, points: Point[]): boolean {
  const bbox = pointsBBox(points);
  if (!bbox) return false;
  const relativePoints = points.map((p) => ({
    x: p.x - bbox.minX,
    y: p.y - bbox.minY,
  }));

  cell.position(bbox.minX, bbox.minY);
  cell.resize(bbox.width, bbox.height);
  cell.attr('body/refPoints', buildRefPoints(relativePoints));

  const data = { ...((cell.get('data') as Record<string, unknown>) || {}) };
  data.points = points;
  data.vertices = points.map((p) => [p.x, p.y] as [number, number]);
  // 드래그 delta 보정의 기준점 — 새 bbox 좌상단으로 다시 잡아야 export 가 맞는다.
  data.originalPosition = { x: bbox.minX, y: bbox.minY };
  cell.set('data', data);
  return true;
}

/** Sequential name like `Zone-3` based on existing zones on the graph. */
/**
 * 새 구역 이름 — 층_번호(B1_1). 번호는 이 층 구역 번호 중 가장 큰 것 다음이고, 이미 있는 이름은
 * 건너뛴다. 층을 모르면 'N구역'.
 */
export function generateZoneName(existingNames: string[], floorName?: string): string {
  const max = existingNames.reduce((m, n) => Math.max(m, Number(zoneNumberFromName(n, 0))), 0);
  return nextFloorZoneName(floorName, existingNames, max + 1);
}

/** Count zones already placed on the graph (by data.properties.kind). */
export function countZonesOnGraph(graph: dia.Graph): number {
  return graph.getCells().filter((cell) => {
    const data = cell.get('data') as
      | { properties?: { kind?: string } }
      | undefined;
    return data?.properties?.kind === 'zone';
  }).length;
}

export function generateEntityHandle(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Weak fallback for older environments. Not cryptographically strong.
  return `zone-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** zone 의 displayed name 추출 (graph traversal). */
export function listExistingZoneNames(graph: dia.Graph): string[] {
  const names: string[] = [];
  graph.getCells().forEach((cell) => {
    const data = cell.get('data') as
      | { properties?: { kind?: string; name?: string } }
      | undefined;
    if (data?.properties?.kind === 'zone' && data.properties.name) {
      names.push(String(data.properties.name));
    }
  });
  return names;
}

export function useZonePolygonDrawing({
  paper,
  graph,
  onError,
  onSuccess,
  onRequestFinalize,
  floorName,
}: UseZonePolygonDrawingArgs): UseZonePolygonDrawingResult {
  const previewRef = useRef<dia.Element | null>(null);
  // 모달 confirm 까지 대기할 pending state — points/context 을 hold.
  const pendingRef = useRef<{
    points: Point[];
    objectTypeId: number;
    projectId: number;
    floorId: string;
    entityHandle: string;
  } | null>(null);
  const mode = useZoneDrawingStore((s) => s.mode);

  const removePreview = useCallback((): void => {
    if (previewRef.current) {
      previewRef.current.remove();
      previewRef.current = null;
    }
  }, []);

  // Toggle crosshair cursor on the paper element while drawing.
  // Also disable pointer events on the SVG cells layer so clicks landing on
  // existing objects fall through to `blank:pointerdown` (= zone 점 추가)
  // instead of selecting/dragging the object underneath.
  useEffect(() => {
    if (!paper) return;
    const el = paper.el as HTMLElement | undefined;
    if (!el) return;
    const previous = el.style.cursor;
    const cellsLayer = el.querySelector('.joint-cells-layer') as SVGGElement | null;
    const previousPointerEvents = cellsLayer?.style.pointerEvents ?? '';
    if (mode === 'drawing') {
      el.style.cursor = 'crosshair';
      if (cellsLayer) cellsLayer.style.pointerEvents = 'none';
    }
    return () => {
      el.style.cursor = previous;
      if (cellsLayer) cellsLayer.style.pointerEvents = previousPointerEvents;
    };
  }, [paper, mode]);

  useEffect(() => {
    if (!paper || !graph) return;
    if (mode !== 'drawing') {
      removePreview();
      return;
    }

    const ensurePreviewCell = (): dia.Element => {
      if (previewRef.current) return previewRef.current;
      const path = new shapes.standard.Path();
      path.attr({
        body: {
          fill: ZONE_DEFAULT_COLOR,
          fillOpacity: ZONE_FILL_OPACITY,
          stroke: ZONE_DEFAULT_COLOR,
          strokeWidth: 2,
          pointerEvents: 'none',
        },
      });
      path.set('data', { [ZONE_PREVIEW_DATA_KEY]: true });
      path.addTo(graph);
      previewRef.current = path;
      return path;
    };

    const syncPreview = (): void => {
      // CANVAS_EDITOR 모드에선 캔버스 오버레이(useCanvasEditorOverlay)가 자체
      // SVG 미리보기를 그리므로 JointJS 프리뷰 셀을 만들지 않는다(이중 렌더 방지).
      if (getFlag('CANVAS_EDITOR')) return;
      const { points, hoverPoint } = useZoneDrawingStore.getState();
      if (points.length === 0) {
        removePreview();
        return;
      }
      const cell = ensurePreviewCell();
      cell.attr('body/d', buildPreviewPath(points, hoverPoint));
    };

    const onPointerDown = (_evt: dia.Event, x: number, y: number): void => {
      useZoneDrawingStore.getState().addPoint({ x, y });
      syncPreview();
    };

    const onPointerMove = (_evt: dia.Event, x: number, y: number): void => {
      useZoneDrawingStore.getState().setHover({ x, y });
      syncPreview();
    };

    paper.on('blank:pointerdown', onPointerDown);
    paper.on('blank:pointermove', onPointerMove);

    const unsubscribe = useZoneDrawingStore.subscribe((state, prev) => {
      if (
        state.points !== prev.points ||
        state.hoverPoint !== prev.hoverPoint
      ) {
        syncPreview();
      }
    });

    return (): void => {
      paper.off('blank:pointerdown', onPointerDown);
      paper.off('blank:pointermove', onPointerMove);
      unsubscribe();
      removePreview();
    };
  }, [paper, graph, mode, removePreview]);

  const cancelDrawing = useCallback((): void => {
    removePreview();
    pendingRef.current = null;
    useZoneDrawingStore.getState().cancel();
  }, [removePreview]);

  /**
   * 완료 버튼 핸들러:
   * - onRequestFinalize 가 있으면 모달을 띄우도록 콜백 호출, store 는 그대로 유지.
   * - 없으면 즉시 finalize(자동 이름) 실행 — 하위 호환.
   */
  const completeDrawing = useCallback(async (): Promise<void> => {
    if (!paper || !graph) return;

    const store = useZoneDrawingStore.getState();
    if (!store.canComplete()) {
      onError?.(new Error('Zone은 최소 3개 점이 필요합니다'));
      return;
    }

    const { objectTypeId, projectId, floorId } = store;
    if (objectTypeId == null || projectId == null || floorId == null) {
      onError?.(new Error('Zone 드로잉 컨텍스트 누락'));
      return;
    }

    // 다시 그리기 — 기존 cell 의 폴리곤만 교체한다. 이름·색·소속이 이미
    // 있으므로 모달을 띄우지 않는다.
    //
    // 서버 저장(saveZoneMapping)도 하지 않는다. 자동 생성 존이 graph.addCell
    // 만 하고 맵 Save 로 영속화되는 것과 같은 경로를 쓴다 — saveZoneMapping 은
    // `/layer-type-mappings` 층 전체 Replace 라, 존 하나 다시 그렸다고 부르면
    // 같은 층의 다른 매핑까지 갈아엎는다.
    if (store.replaceCellId) {
      const replacePoints = store.points.slice();
      const target = graph.getCell(store.replaceCellId);
      useZoneDrawingStore.getState().complete();
      removePreview();
      pendingRef.current = null;
      if (!target || !target.isElement()) {
        onError?.(new Error('다시 그릴 구역을 찾지 못했습니다'));
        return;
      }
      if (!applyZonePoints(target as dia.Element, replacePoints)) {
        onError?.(new Error('zone points 가 비어있음'));
        return;
      }
      // 구역과 CCTV 는 폴리곤 포함으로 자동 연결 — 다시 그린 경계에 맞춰 갱신.
      linkZoneToContainedCctvs(graph, target as dia.Element);
      onSuccess?.(target.id.toString());
      return;
    }

    // 모달용 pending state 보관 — finalizeDrawing 에서 사용
    const points = store.points.slice();
    const entityHandle = generateEntityHandle();
    pendingRef.current = {
      points,
      objectTypeId,
      projectId,
      floorId,
      entityHandle,
    };

    if (onRequestFinalize) {
      // 모달 띄우고 사용자 입력 대기
      onRequestFinalize({
        points,
        suggestedName: generateZoneName(listExistingZoneNames(graph), floorName),
        existingZoneNames: listExistingZoneNames(graph),
      });
      return;
    }

    // 콜백 미제공 시 즉시 finalize (하위 호환)
    await finalizeDrawingImpl({ name: generateZoneName(listExistingZoneNames(graph), floorName) });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paper, graph, onError, onSuccess, onRequestFinalize, removePreview, floorName]);

  const finalizeDrawingImpl = useCallback(
    async (input: ZoneFinalizeInput): Promise<void> => {
      if (!paper || !graph) return;
      const pending = pendingRef.current;
      if (!pending) {
        onError?.(new Error('완료할 zone 이 없습니다'));
        return;
      }
      const { points, objectTypeId, projectId, floorId, entityHandle } = pending;

      const zoneName = (input.name && input.name.trim()) || generateZoneName(listExistingZoneNames(graph), floorName);
      const groupLayer = (input.group && input.group.trim()) || ZONE_LAYER_NAME;

      // store 정리 + preview 제거
      useZoneDrawingStore.getState().complete();
      removePreview();
      pendingRef.current = null;

      // shared helper 로 cell 생성 — restore 경로와 일관성 유지 + 렌더 버그 방지
      const polygon = createZoneCell({
        points,
        name: zoneName,
        objectTypeId,
        entityHandle,
        layerName: groupLayer,
        strokeColor: input.color,
        fontSize: computeLabelFontSize(graph),
      });
      if (!polygon) {
        onError?.(new Error('zone points 가 비어있음'));
        return;
      }

      graph.addCell(polygon);
      // 구역 생성과 동시에 안에 든 CCTV 와 자동으로 관계를 맺는다
      // (자동 관계 생성 버튼의 zone↔CCTV 단계는 제거됨 — 여기가 유일한 경로).
      linkZoneToContainedCctvs(graph, polygon);

      try {
        await zoneApi.saveZoneMapping(projectId, floorId, {
          objectTypeId,
          entityHandle,
          points,
          properties: {
            kind: 'zone',
            closed: true,
            name: zoneName,
            color: input.color,
          },
        });
        onSuccess?.(polygon.id.toString());
      } catch (error: unknown) {
        polygon.remove();
        const err =
          error instanceof Error ? error : new Error('Zone 저장 실패');
        onError?.(err);
      }
    },
    [paper, graph, onError, onSuccess, removePreview, floorName]
  );

  return { completeDrawing, finalizeDrawing: finalizeDrawingImpl, cancelDrawing };
}
